import { z } from "zod";
import { PaginationRequestSchema, PaginationResponseSchema } from "./pagination";
import { ApiResponseSuccessSchema } from "./common";

export const CursorPaginationResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    data: z.array(itemSchema),
    pagination: z.object({
      pageSize: z.number(),
      hasNext: z.boolean(),
      hasPrev: z.boolean(),
      nextCursor: z.object({
        cursorDate: z.string().datetime().nullable(),
        cursorId: z.string(),
      }).nullable(),
      prevCursor: z.object({
        cursorDate: z.string().datetime().nullable(),
        cursorId: z.string(),
      }).nullable(),
    }),
  });

export const FixtureQuerySchema = z.object({
  pageSize: z.coerce.number(),
  cursorDate: z.string().nullable().optional(),
  cursorId: z.string().nullable().optional(),

  direction: z.enum(["forward", "backward"]).optional(),
  pastCount: z.coerce.number().optional(),
  initialLoad: z.preprocess(
    (value) => {
      if (value === "true") return true;
      if (value === "false") return false;
      return value;
    },
    z.boolean().optional(),
  ),

  seasonId: z.string().optional(),
  roundNumber: z.coerce.number().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  knockout: z.preprocess(
    (value) => {
      if (value === "true") return true;
      if (value === "false") return false;
      return value;
    },
    z.boolean().optional(),
  ),
  groupId: z.string().optional(),
});
export type FixtureQueryDTO = z.infer<typeof FixtureQuerySchema>;

export const FixtureResponseSchema = z.object({
  id: z.string(),
  seasonId: z.string().nullable(),
  stageId: z.string().nullable(),
  groupId: z.string().nullable(),
  homeParticipantId: z.string().nullable(),
  awayParticipantId: z.string().nullable(),
  homeSlotId: z.string().nullable(),
  awaySlotId: z.string().nullable(),
  homeSlotKey: z.string().nullable().optional(),
  awaySlotKey: z.string().nullable().optional(),
  homeSlotParentId: z.string().nullable().optional(),
  awaySlotParentId: z.string().nullable().optional(),
  homeSlotTargetId: z.string().nullable().optional(),
  awaySlotTargetId: z.string().nullable().optional(),
  homeSlotParticipantId: z.string().nullable().optional(),
  awaySlotParticipantId: z.string().nullable().optional(),
  homeSlotLabel: z.string().nullable().optional(),
  awaySlotLabel: z.string().nullable().optional(),
  stageMatchOrder: z.number().optional(),
  homeScore: z.number(),
  awayScore: z.number(),
  homePenalties: z.number(),
  awayPenalties: z.number(),
  homeFairPlayScore: z.number().optional().nullable(),
  awayFairPlayScore: z.number().optional().nullable(),
  matchDate: z.coerce.date(),
  statusId: z.string().nullable(),
  winnerId: z.string().nullable(),
  roundNumber: z.number().nullable(),
  venueId: z.string().nullable(),
  group: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  venue: z
    .object({
      id: z.string(),
      name: z.string(),
      address: z.string().nullable(),
      city: z.string().nullable(),
      countryId: z.string().nullable(),
      capacity: z.number().nullable(),
      surface: z.string().nullable(),
      image: z.string().nullable(),
      createdAt: z.coerce.date(),
      updatedAt: z.coerce.date(),
    })
    .nullable()
    .optional(),
  homeTeam: z
    .object({
      id: z.string(),
      name: z.string(),
      code: z.string(),
      countryId: z.string().nullable(),
      founded: z.number().nullable(),
      national: z.boolean(),
      logo: z.string().nullable(),
      createdAt: z.coerce.date(),
      updatedAt: z.coerce.date(),
      country: z
        .object({
          id: z.string(),
          name: z.string(),
          flag: z.string().nullable(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
  awayTeam: z
    .object({
      id: z.string(),
      name: z.string(),
      code: z.string(),
      countryId: z.string().nullable(),
      founded: z.number().nullable(),
      national: z.boolean(),
      logo: z.string().nullable(),
      createdAt: z.coerce.date(),
      updatedAt: z.coerce.date(),
      country: z
        .object({
          id: z.string(),
          name: z.string(),
          flag: z.string().nullable(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
});
export type FixtureResponseDTO = z.infer<typeof FixtureResponseSchema>;

export const FixturesPaginatedResponseSchema = CursorPaginationResponseSchema(
  FixtureResponseSchema
);

export type FixturesPaginatedResponseDTO = z.infer<
  typeof FixturesPaginatedResponseSchema
>;

// ----------------------------------------
// response envelopes for OpenAPI / reuse
// ----------------------------------------

export const FixtureResponseSuccessSchema = ApiResponseSuccessSchema(FixtureResponseSchema);
export type FixtureResponseSuccessDTO = z.infer<typeof FixtureResponseSuccessSchema>;

export const FixturesPaginatedResponseSuccessSchema = ApiResponseSuccessSchema(
  FixturesPaginatedResponseSchema,
);
export type FixturesPaginatedResponseSuccessDTO = z.infer<
  typeof FixturesPaginatedResponseSuccessSchema
>;

// ----------------------------------------
// estructura de árbol de knockout agrupada por etapa
// ----------------------------------------
export const KnockoutStageGroupSchema = z.object({
  stageId: z.string(),
  stageName: z.string(),
  stageOrder: z.number().nullable().optional(),
  fixtures: z.array(FixtureResponseSchema),
});
export type KnockoutStageGroupDTO = z.infer<typeof KnockoutStageGroupSchema>;

export const KnockoutTreeResponseSchema = z.array(KnockoutStageGroupSchema);
export type KnockoutTreeResponseDTO = z.infer<typeof KnockoutTreeResponseSchema>;

export const KnockoutTreeResponseSuccessSchema = ApiResponseSuccessSchema(
  KnockoutTreeResponseSchema,
);
export type KnockoutTreeResponseSuccessDTO = z.infer<
  typeof KnockoutTreeResponseSuccessSchema
>;

export const CreateFixtureRequestSchema = z.object({
  seasonId: z.string().min(1, "La temporada es requerida"),
  stageId: z.string().optional().nullable(),
  groupId: z.string().optional().nullable(),
  homeParticipantId: z.string().optional().nullable(),
  awayParticipantId: z.string().optional().nullable(),
  homeSlotId: z.string().optional().nullable(),
  awaySlotId: z.string().optional().nullable(),
  homeSlotKey: z.string().optional().nullable(),
  awaySlotKey: z.string().optional().nullable(),
  homeScore: z.number().optional(),
  awayScore: z.number().optional(),
  homePenalties: z.number().optional(),
  awayPenalties: z.number().optional(),
  homeFairPlayScore: z.number().optional().nullable(),
  awayFairPlayScore: z.number().optional().nullable(),
  matchDate: z.string().datetime(),
  statusId: z.string().optional().nullable(),
  winnerId: z.string().optional().nullable(),
  roundNumber: z.number().optional().nullable(),
  venueId: z.string().optional().nullable(),
});

export const UpdateFixtureRequestSchema = CreateFixtureRequestSchema.partial().extend({
  updateStandings: z.boolean().optional(),
});

export type CreateFixtureRequestDTO = z.infer<typeof CreateFixtureRequestSchema>;
export type UpdateFixtureRequestDTO = z.infer<typeof UpdateFixtureRequestSchema>;

export const FixtureCatalogSelectionSchema = z.object({
  tournamentFormatId: z.string().min(1, "El formato de torneo es requerido"),
  matchOrder: z.number().int().min(1, "El orden del partido es requerido"),
});

export const ImportFixturesFromCatalogRequestSchema = z.object({
  tournamentId: z.string().min(1, "El torneo es requerido"),
  seasonId: z.string().min(1, "La temporada es requerida"),
  stageId: z.string().nullable().optional(),
  matchSelections: z
    .array(FixtureCatalogSelectionSchema)
    .min(1, "Debes seleccionar al menos un partido"),
});

export type FixtureCatalogSelectionDTO = z.infer<
  typeof FixtureCatalogSelectionSchema
>;
export type ImportFixturesFromCatalogRequestDTO = z.infer<
  typeof ImportFixturesFromCatalogRequestSchema
>;

export const ImportFixturesFromCatalogResponseSchema = z.object({
  createdCount: z.number(),
  skippedCount: z.number(),
  fixtures: z.array(FixtureResponseSchema),
  skipped: z.array(
    z.object({
      tournamentFormatId: z.string(),
      matchOrder: z.number(),
      reason: z.string(),
    }),
  ),
});

export type ImportFixturesFromCatalogResponseDTO = z.infer<
  typeof ImportFixturesFromCatalogResponseSchema
>;

export const ImportFixturesFromCatalogResponseSuccessSchema =
  ApiResponseSuccessSchema(ImportFixturesFromCatalogResponseSchema);
export type ImportFixturesFromCatalogResponseSuccessDTO = z.infer<
  typeof ImportFixturesFromCatalogResponseSuccessSchema
>;

// ======================
// FIXTURE PERIOD
// ======================
export const FixturePeriodResponseSchema = z.object({
  id: z.number(),
  fixtureId: z.string(),
  periodName: z.string(),
  homeScore: z.number().nullable(),
  awayScore: z.number().nullable(),
  periodOrder: z.number(),
});
export type FixturePeriodResponseDTO = z.infer<typeof FixturePeriodResponseSchema>;

export const FixturePeriodsPaginatedResponseSchema = PaginationResponseSchema(FixturePeriodResponseSchema);
export type FixturePeriodsPaginatedResponseDTO = z.infer<typeof FixturePeriodsPaginatedResponseSchema>;

export const CreateFixturePeriodSchema = z.object({
  fixtureId: z.string().min(1, "El ID del partido es requerido"),
  periodName: z.string().min(1, "El nombre del período es requerido"),
  homeScore: z.number().int().min(0).optional().default(0),
  awayScore: z.number().int().min(0).optional().default(0),
  periodOrder: z.number().int().min(1).optional(),
});
export type CreateFixturePeriodDTO = z.infer<typeof CreateFixturePeriodSchema>;

export const UpdateFixturePeriodSchema = z.object({
  periodName: z.string().min(1).optional(),
  homeScore: z.number().int().min(0).optional(),
  awayScore: z.number().int().min(0).optional(),
  periodOrder: z.number().int().min(1).optional(),
});
export type UpdateFixturePeriodDTO = z.infer<typeof UpdateFixturePeriodSchema>;
